import {
  WorkspaceAsset,
  WorkspaceRoutine,
  RoutineConfig,
} from '@gripp/shared-logic';
import { uniqBy } from 'lodash';
import { extractAndDeduplicateRoutines } from './assetHelpers';
import { RoutineModules } from '../components/routine/routineModules';
import { RoutineExecutionProps } from '../components/routine/components/routineButtons';

/**
 * Get auto-prompt routines for an asset, sorted by order.
 * Filters out deactivated routines and one-time routines that have already been submitted.
 */
export function getAutoPromptRoutines(
  workspaceAsset: WorkspaceAsset
): WorkspaceRoutine[] {
  const allExternalRoutines = extractAndDeduplicateRoutines(workspaceAsset);
  const allRoutines = uniqBy(
    [...allExternalRoutines, ...(workspaceAsset.routines || [])],
    'id'
  );

  const hasState = (routineId: string) =>
    workspaceAsset.routineState?.some(
      (s) => s.workspaceRoutineId === routineId
    );

  return allRoutines
    .filter((r) => {
      if (!r) return false;
      if (r.deactivated) return false;
      const config = r.config as any;
      if (!config?.autoPrompt) return false;
      // Skip one-time routines that have already been submitted
      if (config?.oneTimeOnly && hasState(r.id)) return false;
      return true;
    })
    .sort((a, b) => a.order - b.order);
}

/**
 * Build RoutineExecutionProps for a routine.
 */
export function buildRoutineExecutionProps(
  workspaceAsset: WorkspaceAsset,
  routine: WorkspaceRoutine,
  autoPromptQueue?: RoutineExecutionProps[]
): RoutineExecutionProps | null {
  const module =
    routine.type && RoutineModules[routine.type as keyof typeof RoutineModules];
  if (!module) return null;

  const state = workspaceAsset.routineState?.find(
    (s) => s.workspaceRoutineId === routine.id
  );

  return {
    workspaceAsset,
    routine,
    module,
    state,
    autoPromptQueue,
  };
}

/**
 * Get the first auto-prompt routine props with queue of remaining routines.
 * Returns null if no auto-prompt routines exist.
 */
export function getAutoPromptProps(workspaceAsset: WorkspaceAsset): {
  first: RoutineExecutionProps;
  queue: RoutineExecutionProps[];
} | null {
  const routines = getAutoPromptRoutines(workspaceAsset);
  if (routines.length === 0) return null;

  const [firstRoutine, ...remainingRoutines] = routines;

  const queue: RoutineExecutionProps[] = remainingRoutines
    .map((r) => buildRoutineExecutionProps(workspaceAsset, r))
    .filter((props): props is RoutineExecutionProps => props !== null);

  const first = buildRoutineExecutionProps(workspaceAsset, firstRoutine, queue);
  if (!first) return null;

  return { first, queue };
}

/**
 * Pop the next routine from the queue.
 * Returns the next routine props with updated queue, or null if queue is empty.
 */
export function popNextAutoPrompt(
  currentProps: RoutineExecutionProps
): RoutineExecutionProps | null {
  const queue = currentProps.autoPromptQueue;
  if (!queue || queue.length === 0) return null;

  const [next, ...remaining] = queue;
  return {
    ...next,
    autoPromptQueue: remaining,
  };
}
